import React, { useState, useRef, useEffect } from 'react';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { X as XIcon, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable person picker for selecting org members.
 * Shows Orka users (green dot) vs workspace-only members.
 * 
 * Props:
 * - value: string (user_id) for single, string[] for multi
 * - onChange: (value) => void
 * - orgMembers: array of org members with is_orka_user flag
 * - multi: boolean (default false)
 * - placeholder: string
 * - disabled: boolean
 * - className: string
 */
export const PersonPicker = ({
  value,
  onChange,
  orgMembers = [],
  multi = false,
  placeholder = 'Select person...',
  disabled = false,
  className = '',
  'data-testid': testId = 'person-picker',
}) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = orgMembers.filter(m => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q);
  });

  // For multi mode, exclude already-selected
  const available = multi
    ? filtered.filter(m => !(value || []).includes(m.user_id))
    : filtered;

  const getMemberLabel = (userId) => {
    const m = orgMembers.find(x => x.user_id === userId);
    return m?.name || m?.email || userId;
  };

  const getMember = (userId) => orgMembers.find(x => x.user_id === userId);

  // Single select display
  const selectedMember = !multi ? orgMembers.find(m => m.user_id === value) : null;

  return (
    <div ref={containerRef} className={`relative ${className}`} data-testid={testId}>
      {/* Multi mode: show badges */}
      {multi && (value || []).length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {(value || []).map(uid => {
            const m = getMember(uid);
            return (
              <Badge key={uid} variant="secondary" className="pl-1.5 pr-0.5 py-0.5 text-xs gap-1 items-center" data-testid={`person-badge-${uid}`}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full mr-0.5 ${m?.is_orka_user ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                {getMemberLabel(uid)}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => onChange((value || []).filter(v => v !== uid))}
                    className="ml-0.5 hover:bg-destructive/20 rounded-full p-0.5"
                  >
                    <XIcon className="w-3 h-3" />
                  </button>
                )}
              </Badge>
            );
          })}
        </div>
      )}

      {/* Input */}
      <div className="relative">
        <Input
          value={multi ? search : (open ? search : (selectedMember?.name || selectedMember?.email || (value ? getMemberLabel(value) : '')))}
          onChange={(e) => {
            setSearch(e.target.value);
            if (!open) setOpen(true);
            if (!multi && e.target.value === '') {
              onChange('', null);
            }
          }}
          onFocus={() => { setOpen(true); if (!multi) setSearch(''); }}
          onBlur={() => { setTimeout(() => setOpen(false), 200); }}
          placeholder={placeholder}
          disabled={disabled}
          className="h-9"
          autoComplete="off"
          data-testid={`${testId}-input`}
        />
        {/* Clear button for single select */}
        {!multi && value && !disabled && !open && (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 text-muted-foreground hover:text-foreground transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onChange('', null);
              setSearch('');
            }}
            data-testid={`${testId}-clear`}
          >
            <XIcon className="w-3.5 h-3.5" />
          </button>
        )}
        {/* Orka user indicator on single select (only when no clear button) */}
        {!multi && selectedMember && !open && !value && (
          <span className={`absolute right-3 top-1/2 -translate-y-1/2 inline-block w-2 h-2 rounded-full ${selectedMember.is_orka_user ? 'bg-emerald-500' : 'bg-slate-300'}`} />
        )}
      </div>

      {/* Dropdown */}
      {open && !disabled && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 orka-surface-raised border rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid={`${testId}-dropdown`}>
          {available.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">No members found</div>
          ) : (
            available.slice(0, 12).map(m => (
              <button
                key={m.user_id || m.email}
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-2"
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (multi) {
                    onChange([...(value || []), m.user_id]);
                    setSearch('');
                  } else {
                    onChange(m.user_id, m);
                    setSearch('');
                    setOpen(false);
                  }
                }}
              >
                {/* Avatar with Orka indicator */}
                <div className="relative flex-shrink-0">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${m.is_orka_user ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {(m.name || m.email || '?')[0].toUpperCase()}
                  </div>
                  {m.is_orka_user && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center">
                      <Check className="w-1.5 h-1.5 text-white" />
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate flex items-center gap-1.5">
                    {m.name || m.email}
                    {m.is_orka_user && (
                      <span className="text-[10px] px-1 py-0 bg-emerald-50 text-emerald-600 rounded font-medium">Orka</span>
                    )}
                  </div>
                  {m.email && m.name && (
                    <div className="text-xs text-slate-400 truncate">{m.email}</div>
                  )}
                </div>
                {m.role && m.role !== 'Workspace' && (
                  <span className="text-[10px] text-slate-400">{m.role}</span>
                )}
                {m.role === 'Workspace' && (
                  <span className="text-[10px] text-slate-300">Workspace</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default PersonPicker;
